import React, { useEffect, useRef, useState } from 'react';
import { regenApi } from '../lib/api';
import { toDownloadUrl } from '../lib/utils';
import { Loader2, Image as ImageIcon, Download, Sparkles, AlertTriangle, LayoutTemplate } from 'lucide-react';

// Creative Lab — image generation over the already-wired models.
//  • Free image: plain text → image (Imagen 4 / GPT Image / FLUX / Ideogram).
//  • Ad creative: the compositor path — picks our real archetype (advertorial / threat-poster / organic-snap),
//    builds the structured base photo, and burns the headline / CTA / trust-row on top. This is how our org's
//    DR creatives are actually made, so they come out accurate (not a generic image).
const MODELS = [
  { id: '', label: 'Imagen 4 — photoreal (default)' },
  { id: 'openai:gpt-image-1', label: 'GPT Image — best text in-image' },
  { id: 'flux', label: 'FLUX — stylised / artistic' },
  { id: 'ideogram', label: 'Ideogram — sharpest text & logos' },
];
const ASPECTS = ['1:1', '9:16', '16:9', '4:3'];
// Our real ad archetypes (image_compositor). Each pins the base-photo style; aspect is the archetype default.
const AD_TYPES = [
  { id: 'advertorial_square', label: 'Advertorial (health / supplement)', aspect: '1:1' },
  { id: 'threat_poster', label: 'Threat poster (guns / CCW / self-defense)', aspect: '9:16' },
  { id: 'organic_snap', label: 'Organic snap (auto / everyday UGC)', aspect: '9:16' },
];

type Plan = { format?: string; headline?: string; cta?: string; bullets?: string[]; subject?: string; aspect?: string; cost_estimate?: number };
type Img = { id: string; status: string; url?: string; download?: string; model?: string; plan?: Plan };

export function CreativeLab() {
  const [mode, setMode] = useState<'free' | 'ad' | 'auto'>('free');
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('');
  const [aspect, setAspect] = useState('1:1');
  const [quad, setQuad] = useState(false);
  // ad-creative fields
  const [adType, setAdType] = useState('advertorial_square');
  const [headline, setHeadline] = useState('');
  const [cta, setCta] = useState('');
  const [subject, setSubject] = useState('');
  const [bullets, setBullets] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [imgs, setImgs] = useState<Img[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // When the ad type changes, default the aspect to that archetype's ratio (user can still override).
  useEffect(() => { if (mode === 'ad') { const t = AD_TYPES.find(t => t.id === adType); if (t) setAspect(t.aspect); } }, [adType, mode]);

  // 'planned' is TERMINAL (a preview awaiting confirm), so it is NOT pending — otherwise it polls forever.
  const isPending = (s: string) => s !== 'ready' && s !== 'failed' && s !== 'planned';
  useEffect(() => {
    const pending = imgs.filter(i => isPending(i.status));
    if (!pending.length) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const cur = imgs.filter(i => isPending(i.status));
      if (!cur.length) return;
      await Promise.all(cur.map(async (i) => {
        try {
          const r = await regenApi.getRequest(i.id);
          const req = r.data?.data || r.data;
          const v = (req?.variants || [])[0];
          const url = v?.play_url || v?.video_url || v?.url;
          setImgs(prev => prev.map(x => x.id === i.id
            ? { ...x, status: req?.status || x.status, url: url || x.url, download: v?.download_url || url || x.download, model: req?.model || x.model, plan: req?.plan || x.plan }
            : x));
        } catch { /* keep polling */ }
      }));
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [imgs]);

  // Confirm a planner PREVIEW → render the chosen photoreal format with its copy (this is the paid call).
  const confirmPlan = async (img: Img) => {
    const pl = img.plan; if (!pl || busy) return;
    setBusy(true); setErr('');
    try {
      const body: any = { mode: 'ad', format: pl.format, confirm: true, aspect: pl.aspect || aspect, count: 1,
        prompt: pl.headline || prompt || pl.format };
      if (pl.headline) body.headline = pl.headline;
      if (pl.cta) body.cta = pl.cta;
      if (pl.subject) body.subject = pl.subject;
      if (pl.bullets?.length) body.bullets = pl.bullets;
      const r = await regenApi.generateImages(body);
      const ids: string[] = r.data?.data?.ids || [];
      if (!ids.length) throw new Error('nothing dispatched');
      setImgs(prev => [...ids.map(id => ({ id, status: 'running' as const })), ...prev.filter(x => x.id !== img.id)]);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Could not generate — try again.');
    } finally { setBusy(false); }
  };

  const generate = async () => {
    const p = prompt.trim();
    if (busy) return;
    if (mode === 'ad' && !headline.trim() && !p) { setErr('Add a headline (or a prompt) for the ad.'); return; }
    if (mode === 'free' && !p) { setErr('Describe the image first.'); return; }
    if (mode === 'auto' && !p) { setErr('Describe the creative you want in plain words.'); return; }
    setBusy(true); setErr('');
    try {
      const body: any = { prompt: p || headline, image_model: model || undefined, aspect, count: quad ? 4 : 1 };
      if (mode === 'auto') {
        // Planner picks the format + writes the copy. Graphic formats render at $0; photoreal returns a
        // preview (status 'planned') with a Generate button, so nothing paid happens un-previewed.
        body.plan = true; body.count = 1; delete body.image_model;
      } else if (mode === 'ad') {
        body.mode = 'ad';
        body.template = adType;
        if (headline.trim()) body.headline = headline.trim();
        if (cta.trim()) body.cta = cta.trim();
        if (subject.trim()) body.subject = subject.trim();
        const b = bullets.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
        if (b.length) body.bullets = b;
      }
      const r = await regenApi.generateImages(body);
      const ids: string[] = r.data?.data?.ids || [];
      if (!ids.length) throw new Error('nothing dispatched');
      setImgs(prev => [...ids.map(id => ({ id, status: 'running' as const })), ...prev]);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Could not start — try again.');
    } finally { setBusy(false); }
  };

  const aspectClass = (a: string) => a === '9:16' ? 'aspect-[9/16]' : a === '16:9' ? 'aspect-[16/9]' : a === '4:3' ? 'aspect-[4/3]' : 'aspect-square';
  const tab = (m: 'free' | 'ad' | 'auto', label: string, Icon: any) => (
    <button type="button" onClick={() => setMode(m)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${mode === m ? 'border-primary/60 text-primary bg-primary/10' : 'border-border text-gray-400 hover:text-gray-200'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto w-full py-4 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Creative Lab — Images</h2>
        <p className="text-sm text-gray-400 mt-1">Free image = any picture. Ad creative = our real DR archetypes (advertorial / threat-poster / organic-snap) with the headline and CTA burned on. Nothing generates until you hit Generate.</p>
      </div>

      <div className="flex items-center gap-2">{tab('auto', 'Auto (describe it)', Sparkles)}{tab('free', 'Free image', ImageIcon)}{tab('ad', 'Ad creative', LayoutTemplate)}</div>

      <div className="rounded-2xl border border-border bg-card p-3 space-y-3">
        {mode === 'ad' && (
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="text-xs text-gray-400 space-y-1"><span>Ad type</span>
              <select value={adType} onChange={e => setAdType(e.target.value)} className="w-full rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-gray-200">
                {AD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-400 space-y-1"><span>Headline (burned on the ad)</span>
              <input value={headline} onChange={e => setHeadline(e.target.value)} placeholder='e.g. "Seniors Are Switching to This $29 Plan"'
                className="w-full rounded-lg border border-border bg-transparent px-2 py-1.5 text-sm text-white placeholder:text-gray-500 focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400 space-y-1"><span>CTA button</span>
              <input value={cta} onChange={e => setCta(e.target.value)} placeholder='e.g. "Check Your Rate"'
                className="w-full rounded-lg border border-border bg-transparent px-2 py-1.5 text-sm text-white placeholder:text-gray-500 focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400 space-y-1"><span>Subject / who's in the photo (optional)</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder='e.g. "a smiling woman in her 60s in a bright kitchen"'
                className="w-full rounded-lg border border-border bg-transparent px-2 py-1.5 text-sm text-white placeholder:text-gray-500 focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400 space-y-1 sm:col-span-2"><span>Trust-row bullets (optional, comma or newline separated)</span>
              <input value={bullets} onChange={e => setBullets(e.target.value)} placeholder='e.g. "No medical exam, Rates from $29/mo, A+ rated"'
                className="w-full rounded-lg border border-border bg-transparent px-2 py-1.5 text-sm text-white placeholder:text-gray-500 focus:outline-none" />
            </label>
          </div>
        )}
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={mode === 'ad' ? 2 : 3}
          placeholder={mode === 'ad'
            ? 'Extra scene direction / offer context (optional) — the ad type + headline drive the look.'
            : mode === 'auto'
              ? 'Say what you want in plain words — e.g. "a yes/no poll asking homeowners if their roof is over 10 years old" or "a breaking-news style ad about a 2026 stimulus". The system picks the right format and writes the copy.'
              : 'e.g. "a smiling grandma holding a medicare card, bright kitchen, photoreal, soft daylight"'}
          className="w-full rounded-xl border border-border bg-transparent p-3 text-sm text-white placeholder:text-gray-500 resize-none focus:outline-none" />
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'free' && (
            <select value={model} onChange={e => setModel(e.target.value)} className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-gray-200">
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          )}
          <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5">
            {ASPECTS.map(a => (
              <button key={a} type="button" onClick={() => setAspect(a)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium ${aspect === a ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:text-gray-200'}`}>{a}</button>
            ))}
          </div>
          <button type="button" onClick={() => setQuad(v => !v)} title="Generate 4 variants at once"
            className={`px-2.5 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1 ${quad ? 'border-primary/60 text-primary bg-primary/10' : 'border-border text-gray-400 hover:bg-accent'}`}>
            <Sparkles className="w-3.5 h-3.5" /> Quad (4){quad ? ' ✓' : ''}
          </button>
          <button type="button" onClick={generate} disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} {mode === 'auto' ? 'Plan it' : <>Generate {quad ? '4' : ''} {mode === 'ad' ? 'ad creative' : 'image'}{quad ? 's' : ''}</>}
          </button>
        </div>
        {err && <div className="text-xs text-red-400 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}
      </div>

      {imgs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {imgs.map(i => (
            <div key={i.id} className={`relative rounded-xl border border-border bg-black/40 overflow-hidden ${aspectClass(aspect)}`}>
              {i.url
                ? <img src={i.url} alt="" className="w-full h-full object-contain" loading="lazy" />
                : i.status === 'planned' && i.plan
                  ? <div className="w-full h-full flex flex-col justify-between p-2 text-left">
                      <div className="text-[11px] text-gray-300 space-y-1 overflow-auto">
                        <div className="text-primary font-medium">Plan · {i.plan.format}</div>
                        {i.plan.headline && <div className="text-white leading-snug">{i.plan.headline}</div>}
                        {i.plan.cta && <div className="text-gray-400">CTA: {i.plan.cta}</div>}
                        {i.plan.subject && <div className="text-gray-500 text-[10px] leading-snug">{i.plan.subject}</div>}
                      </div>
                      <button type="button" onClick={() => confirmPlan(i)} disabled={busy}
                        className="mt-1 inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Generate (~${(i.plan.cost_estimate ?? 0.04).toFixed(2)})
                      </button>
                    </div>
                  : i.status === 'failed'
                    ? <div className="w-full h-full flex items-center justify-center text-xs text-red-400 px-2 text-center"><AlertTriangle className="w-4 h-4 mr-1" /> failed</div>
                    : <div className="w-full h-full flex items-center justify-center text-xs text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
              {i.url && (
                <a href={toDownloadUrl(i.download || i.url, `image_${i.id}.png`)} download
                  className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-lg bg-black/70 px-2 py-1 text-[11px] text-white hover:bg-black/90">
                  <Download className="w-3.5 h-3.5" /> Save
                </a>
              )}
              {i.model && i.url && <span className="absolute top-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-gray-200 font-mono">{i.model}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
